import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Send, Loader2, CheckCircle2, Paperclip, ExternalLink, AtSign, UserPlus, Crown, X, History, Sparkles, RotateCcw, GripVertical, Pencil, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentList, AttachmentDisplay, readFileAsBase64, fmtDate } from './attachments';
import UserAvatar from '@/components/UserAvatar';
import { onMessagingEvent } from '@/lib/messagingSocket';

const REACTIONS = ['👍', '✅', '❤️', '🎉', '👀'];
const REACTION_NAME = { '👍': 'thumbsup', '✅': 'check', '❤️': 'heart', '🎉': 'party', '👀': 'eyes' };

function ReactionBar({ reactions, myId, canWrite, onToggle, idPrefix, float = false, align = 'start' }) {
  const [open, setOpen] = useState(false);
  const active = REACTIONS.filter(e => (reactions?.[e] || []).length > 0);
  if (!canWrite && active.length === 0) return null;
  // In `float` mode the bar sits absolutely over the bubble's bottom corner
  // (Teams-style). The add-button stays hidden until the message is hovered so
  // it doesn't clutter every bubble.
  const wrapCls = float
    ? `absolute -bottom-2.5 ${align === 'end' ? 'right-2' : 'left-2'} flex items-center gap-1 z-10`
    : 'flex items-center gap-1 mt-1.5 flex-wrap';
  return (
    <div className={wrapCls}>
      {active.map(e => {
        const list = reactions[e] || [];
        const mine = list.includes(myId);
        const base = mine ? 'bg-primary/20 border-primary/40 text-primary' : 'bg-muted/30 border-border/40 text-muted-foreground';
        const floated = mine ? 'bg-primary/25 border-primary/50 text-primary shadow-sm' : 'bg-card border-border/60 text-foreground shadow-sm';
        return (
          <button key={e} onClick={() => canWrite && onToggle(e)} disabled={!canWrite} data-testid={`reaction-${idPrefix}-${REACTION_NAME[e]}`}
            className={`flex items-center gap-1 h-5 px-1.5 rounded-full text-[10px] border transition-colors ${float ? floated : base} ${canWrite ? 'hover:text-foreground cursor-pointer' : 'cursor-default'}`}>
            <span>{e}</span><span>{list.length}</span>
          </button>
        );
      })}
      {canWrite && (
      <div className={`relative ${float && active.length > 0 ? 'opacity-0 group-hover:opacity-100 transition-opacity' : ''}`}>
        <button onClick={() => setOpen(o => !o)} data-testid={`reaction-add-${idPrefix}`}
          className={`h-5 px-1.5 rounded-full text-[10px] border border-border/40 text-muted-foreground hover:text-foreground ${float ? 'bg-card shadow-sm' : 'bg-muted/30'}`}>😊 +</button>
        {open && (
          <div className={`absolute z-50 bottom-6 ${align === 'end' ? 'right-0' : 'left-0'} flex gap-0.5 p-1 rounded-lg border border-border/50 bg-popover shadow-lg`} data-testid={`reaction-picker-${idPrefix}`}>
            {REACTIONS.map(e => (
              <button key={e} onClick={() => { onToggle(e); setOpen(false); }} data-testid={`reaction-pick-${idPrefix}-${REACTION_NAME[e]}`}
                className="w-6 h-6 rounded hover:bg-muted/50 text-sm">{e}</button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export function DiscussionThreadDialog({ open, onOpenChange, discussionId, floating = false, inline = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const myId = user?.user_id;
  const canWrite = (user?.permissions || []).includes('discussions:write');
  const [disc, setDisc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [showMention, setShowMention] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const replyFileRef = React.useRef(null);

  const nameOf = (uid) => {
    const u = users.find(x => x.user_id === uid);
    return u ? (u.name || u.email || uid) : uid;
  };
  const avatarOf = (uid) => {
    const u = users.find(x => x.user_id === uid);
    return u?.avatarUrl || '';
  };

  const eventLabel = (ev) => {
    const who = ev.actor_name || nameOf(ev.actor_id);
    const tgt = (ev.target_ids || []).map(nameOf).join(', ');
    switch (ev.kind) {
      case 'created': return `${who} started the discussion`;
      case 'replied': return `${who} replied`;
      case 'mentioned': return `${who} mentioned ${tgt}`;
      case 'participant_added': return `${who} added ${tgt}`;
      case 'participant_removed': return `${who} removed ${tgt}`;
      case 'resolved': return `${who} resolved the discussion`;
      case 'reopened': return `${who} reopened the discussion`;
      case 'archived': return `${who} archived the discussion`;
      default: return `${who} — ${ev.kind}`;
    }
  };

  const addMention = (u) => {
    setMentions(prev => prev.includes(u.user_id) ? prev : [...prev, u.user_id]);
    const handle = (u.name || u.email || u.user_id).replace(/\s+/g, '');
    setReplyText(prev => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}@${handle} `);
    setShowMention(false);
  };

  const toggleParticipant = async (uid, remove) => {
    try {
      const updated = await api.addDiscussionParticipant(discussionId, uid, remove);
      setDisc(updated);
      setShowAddParticipant(false);
    } catch (err) { toast.error(err.message); }
  };

  const handleReplyFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB limit`); continue; }
      const dataUrl = await readFileAsBase64(file);
      setReplyAttachments(prev => [...prev, { name: file.name, size: file.size, type: file.type, data_url: dataUrl }]);
    }
    if (replyFileRef.current) replyFileRef.current.value = '';
  };

  useEffect(() => {
    if (open && discussionId) {
      setLoading(true);
      setMentions([]);
      api.getDiscussion(discussionId).then(setDisc).catch(() => {}).finally(() => setLoading(false));
      api.getMentionableUsers().then(setUsers).catch(() => {});
      api.markDiscussionRead(discussionId).catch(() => {});
    }
  }, [open, discussionId]);

  // Live replies — append in real time when another participant posts (dedupe
  // by reply_id so the author's own optimistic re-fetch doesn't double-render).
  useEffect(() => {
    if (!open || !discussionId) return;
    return onMessagingEvent((evt) => {
      if (evt.type === 'discussion_reply' && evt.discussion_id === discussionId && evt.reply) {
        setDisc(prev => {
          if (!prev) return prev;
          if ((prev.replies || []).some(r => r.reply_id === evt.reply.reply_id)) return prev;
          return { ...prev, replies: [...(prev.replies || []), evt.reply] };
        });
        api.markDiscussionRead(discussionId).catch(() => {});
      }
    });
  }, [open, discussionId]);

  // Inline @mention autocomplete — matches a trailing "@partial" as the user types.
  const mentionMatch = /@([\w.\-]*)$/.exec(replyText);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionSuggestions = mentionQuery === null ? [] : users.filter(u => {
    const s = `${u.name || ''} ${u.email || ''}`.toLowerCase();
    return s.includes(mentionQuery);
  }).slice(0, 6);

  const selectMention = (u) => {
    const handle = (u.name || u.email || u.user_id).replace(/\s+/g, '');
    setReplyText(prev => prev.replace(/@([\w.\-]*)$/, `@${handle} `));
    setMentions(prev => prev.includes(u.user_id) ? prev : [...prev, u.user_id]);
  };

  const handleReopen = async () => {
    try {
      await api.reopenDiscussion(discussionId);
      const updated = await api.getDiscussion(discussionId);
      setDisc(updated);
      toast.success('Discussion reopened');
    } catch (err) { toast.error(err.message); }
  };

  const toggleReaction = async (emoji, replyId) => {
    try {
      await api.toggleDiscussionReaction(discussionId, emoji, replyId);
      const updated = await api.getDiscussion(discussionId);
      setDisc(updated);
    } catch (err) { toast.error(err.message); }
  };

  const handleReply = async () => {
    if (!replyText.trim() && !replyAttachments.length) return;
    setSending(true);
    try {
      await api.replyToDiscussion(discussionId, replyText, replyAttachments.map(a => ({ name: a.name, size: a.size, type: a.type, data_url: a.data_url })), mentions);
      setReplyText('');
      setReplyAttachments([]);
      setMentions([]);
      const updated = await api.getDiscussion(discussionId);
      setDisc(updated);
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const handleResolve = async () => {
    try {
      await api.resolveDiscussion(discussionId);
      const updated = await api.getDiscussion(discussionId);
      setDisc(updated);
      toast.success('Discussion resolved');
    } catch (err) { toast.error(err.message); }
  };

  const handleRename = async () => {
    try {
      const updated = await api.renameDiscussion(discussionId, titleDraft.trim());
      setDisc(updated);
      setRenaming(false);
      toast.success('Group renamed');
    } catch (err) { toast.error(err.message); }
  };

  const handleLeave = async () => {
    try {
      await api.addDiscussionParticipant(discussionId, myId, true);
      toast.success('You left the discussion');
      onOpenChange(false);
    } catch (err) { toast.error(err.message); }
  };

  const inner = (
    <>
      {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 text-primary animate-spin mx-auto" /></div> : disc && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <OriginalQuestion disc={disc} onOpenSource={() => {
              const src = disc.report_state?.source;
              if (src === 'investigation') {
                const iid = disc.data_context?.investigation_id || '';
                navigate(iid ? `/investigations?open=${iid}` : '/investigations');
              } else if (src === 'explore') {
                navigate(`/ai-explore?discussion=${disc.discussion_id}`);
              } else if (src === 'insight') {
                const iid = disc.data_context?.insight_id || '';
                navigate(iid ? `/ai-insights?insight_id=${iid}` : '/ai-insights');
              } else if (disc.report_id) {
                navigate(`/reports/view?id=${disc.report_id}&discussion=${disc.discussion_id}`);
              }
              // From the modal, hand off to a floating panel so the user can read
              // the resource AND keep replying. When already floating, just navigate.
              if (!floating) {
                onOpenChange(false);
                window.dispatchEvent(new CustomEvent('discussion:open-floating', { detail: { discussionId: disc.discussion_id } }));
              }
            }} />

            <ReactionBar reactions={disc.reactions} myId={myId} canWrite={canWrite} idPrefix="root" onToggle={(e) => toggleReaction(e)} />

            {disc.status === 'resolved' && disc.resolution_summary && (
              <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 mb-3" data-testid="resolution-summary">
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3 h-3 text-emerald-400" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Resolution recap</span>
                </div>
                <p className="text-xs text-foreground/90 leading-relaxed">{disc.resolution_summary}</p>
              </div>
            )}

            {/* Rename — owners can (re)name a group thread at any time. */}
            {myId === disc.owner && (
              <div className="flex items-center gap-1.5 mb-2" data-testid="rename-discussion-row">
                {renaming ? (
                  <>
                    <Input value={titleDraft} onChange={e => setTitleDraft(e.target.value)} placeholder="Group name" className="h-7 text-xs" data-testid="rename-discussion-input" autoFocus />
                    <Button size="sm" className="h-7 px-2 text-[10px]" onClick={handleRename} disabled={!titleDraft.trim()} data-testid="rename-discussion-save">Save</Button>
                    <button className="text-muted-foreground hover:text-foreground" onClick={() => setRenaming(false)} data-testid="rename-discussion-cancel"><X className="w-3.5 h-3.5" /></button>
                  </>
                ) : (
                  <button onClick={() => { setTitleDraft(disc.title || ''); setRenaming(true); }} className="text-[10px] flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="rename-discussion-btn">
                    <Pencil className="w-3 h-3" /> {disc.title ? 'Rename group' : 'Name this group'}
                  </button>
                )}
              </div>
            )}

            {/* Participants — owner + watchers; anyone who replies auto-follows. */}
            <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="discussion-participants">
              {(disc.assigned_to || []).map(uid => (
                <Badge key={uid} variant="outline" className="text-[9px] gap-1 pr-1">
                  {uid === disc.owner && <Crown className="w-2.5 h-2.5 text-amber-400" />}
                  {nameOf(uid)}
                  {uid !== disc.owner && canWrite && (
                    <button onClick={() => toggleParticipant(uid, true)} className="ml-0.5 hover:text-red-400" data-testid={`remove-participant-${uid}`}><X className="w-2.5 h-2.5" /></button>
                  )}
                </Badge>
              ))}
              {canWrite && (
              <div className="relative">
                <button onClick={() => setShowAddParticipant(v => !v)} className="text-[9px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-dashed border-border/50 text-muted-foreground hover:text-foreground" data-testid="add-participant-btn"><UserPlus className="w-2.5 h-2.5" /> Add</button>
                {showAddParticipant && (
                  <div className="absolute z-50 mt-1 w-48 max-h-48 overflow-auto rounded-lg border border-border/50 bg-popover shadow-lg p-1" data-testid="add-participant-menu">
                    {users.filter(u => !(disc.assigned_to || []).includes(u.user_id)).map(u => (
                      <button key={u.user_id} onClick={() => toggleParticipant(u.user_id, false)} className="w-full text-left px-2 py-1.5 rounded text-[11px] text-foreground hover:bg-muted/50">{u.name || u.email}</button>
                    ))}
                  </div>
                )}
              </div>
              )}
              {myId && myId !== disc.owner && (disc.assigned_to || []).includes(myId) && (
                <button onClick={handleLeave} className="text-[9px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-dashed border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="leave-discussion-btn"><LogOut className="w-2.5 h-2.5" /> Leave</button>
              )}
            </div>

            {/* Activity trail — who was mentioned, replied, added, or resolved and when. */}
            {(disc.events || []).length > 0 && (
              <div className="mb-2">
                <button onClick={() => setShowActivity(v => !v)} className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground" data-testid="activity-trail-toggle">
                  <History className="w-3 h-3" /> Activity ({disc.events.length}) {showActivity ? '▾' : '▸'}
                </button>
                {showActivity && (
                  <div className="mt-1.5 pl-1 space-y-1.5 border-l border-border/40 ml-1" data-testid="activity-trail">
                    {[...disc.events].sort((a, b) => new Date(a.at) - new Date(b.at)).map(ev => (
                      <div key={ev.event_id} className="flex items-center gap-2 pl-2.5 relative">
                        <span className="absolute -left-[3px] w-1.5 h-1.5 rounded-full bg-primary/50" />
                        <span className="text-[10px] text-foreground/80">{eventLabel(ev)}</span>
                        <span className="text-[9px] text-muted-foreground/80 ml-auto whitespace-nowrap">{fmtDate(ev.at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <ScrollArea className="flex-1 mb-3">
              <div className="space-y-3 pr-1">
                {(disc.replies || []).map(r => {
                  const mine = (r.user_id && r.user_id === myId) || (!r.user_id && r.user_name === nameOf(myId));
                  return (
                    <div key={r.reply_id} className={`group flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`discussion-reply-${r.reply_id}`}>
                      {!mine && (
                        <div className="w-6 h-6 shrink-0 mb-4" title={r.user_name}>
                          <UserAvatar name={r.user_name} src={avatarOf(r.user_id)} size={24} />
                        </div>
                      )}
                      <div className={`max-w-[78%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                        <div className="relative pb-2.5">
                          <div className={`px-3 py-2 border ${mine
                            ? 'bg-primary/15 border-primary/25 rounded-2xl rounded-br-md'
                            : 'bg-muted/30 border-border/20 rounded-2xl rounded-bl-md'}`}>
                            {!mine && <div className="text-[10px] font-semibold text-foreground/90 mb-0.5">{r.user_name}</div>}
                            <p className="text-xs text-foreground/90 whitespace-pre-wrap break-words">{r.text}</p>
                            <AttachmentDisplay attachments={r.attachments} />
                          </div>
                          <ReactionBar reactions={r.reactions} myId={myId} canWrite={canWrite} idPrefix={r.reply_id} onToggle={(e) => toggleReaction(e, r.reply_id)} float align={mine ? 'end' : 'start'} />
                        </div>
                        <span className="text-[9px] text-muted-foreground/70 mt-0.5">{fmtDate(r.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                {(!disc.replies || disc.replies.length === 0) && <p className="text-[10px] text-muted-foreground text-center py-3">No replies yet</p>}
              </div>
            </ScrollArea>

            {!canWrite ? (
              <div className="pt-2 border-t border-border/30 flex items-center justify-between" data-testid="discussion-readonly-note">
                <span className="text-[10px] text-muted-foreground">Read-only — your role cannot post or resolve discussions</span>
                {disc.status === 'resolved' && <span className="text-[10px] text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Resolved</span>}
              </div>
            ) : disc.status !== 'resolved' ? (
              <div className="pt-2 border-t border-border/30 space-y-1.5">
                <div className="flex gap-2 relative items-end">
                  {mentionSuggestions.length > 0 && (
                    <div className="absolute bottom-11 left-0 z-50 w-56 max-h-48 overflow-auto rounded-lg border border-border/50 bg-popover shadow-lg p-1" data-testid="mention-autocomplete">
                      {mentionSuggestions.map(u => (
                        <button key={u.user_id} onClick={() => selectMention(u)} className="w-full text-left px-2 py-1.5 rounded text-[11px] text-foreground hover:bg-muted/50 flex items-center gap-2" data-testid={`mention-suggestion-${u.user_id}`}>
                          <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[8px] font-bold text-primary">{(u.name || u.email || '?')[0]?.toUpperCase()}</span>
                          {u.name || u.email}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={replyText}
                    rows={1}
                    onChange={e => { setReplyText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'; }}
                    placeholder="Write a reply... use @ to mention (Shift+Enter for a new line)"
                    className="flex-1 resize-none min-h-[36px] max-h-40 overflow-y-auto rounded-lg bg-muted/50 border border-border/50 text-xs px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && mentionSuggestions.length === 0) { e.preventDefault(); handleReply(); } }}
                    data-testid="discussion-reply-input" />
                  <input ref={replyFileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleReplyFileSelect} className="hidden" />
                  <div className="relative">
                    <Button variant="outline" size="icon" onClick={() => setShowMention(v => !v)} className="rounded-lg w-9 h-9 flex-shrink-0" title="Mention someone" data-testid="mention-btn"><AtSign className="w-3.5 h-3.5" /></Button>
                    {showMention && (
                      <div className="absolute bottom-11 right-0 z-50 w-48 max-h-48 overflow-auto rounded-lg border border-border/50 bg-popover shadow-lg p-1" data-testid="mention-menu">
                        {users.map(u => (
                          <button key={u.user_id} onClick={() => addMention(u)} className="w-full text-left px-2 py-1.5 rounded text-[11px] text-foreground hover:bg-muted/50" data-testid={`mention-user-${u.user_id}`}>{u.name || u.email}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => replyFileRef.current?.click()} className="rounded-lg w-9 h-9 flex-shrink-0" title="Attach file"><Paperclip className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" onClick={handleReply} disabled={sending || (!replyText.trim() && !replyAttachments.length)} className="rounded-lg w-9 h-9">
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </Button>
                  <Button onClick={handleResolve} className="rounded-lg text-xs h-9 px-3 bg-emerald-600 text-white hover:bg-emerald-700 border-0 font-semibold" data-testid="resolve-discussion-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resolve
                  </Button>
                </div>
                <AttachmentList attachments={replyAttachments} onRemove={i => setReplyAttachments(prev => prev.filter((_, idx) => idx !== i))} />
              </div>
            ) : (
              <div className="pt-2 border-t border-border/30 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Resolved{disc.resolved_at ? ` · ${fmtDate(disc.resolved_at)}` : ''}</span>
                <Button variant="outline" size="sm" onClick={handleReopen} className="h-8 rounded-lg text-xs gap-1.5 border-amber-500/40 text-amber-500 hover:bg-amber-500/10" data-testid="reopen-discussion-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Reopen
                </Button>
              </div>
            )}
          </div>
        )}
    </>
  );

  // Inline mode: render the thread content directly inside a host container
  // (the Messages page right pane) instead of a modal/floating window. A compact
  // header bar (title + status + participant avatars) matches the chat header.
  if (inline) {
    if (!open) return null;
    const members = disc?.assigned_to || [];
    return (
      <div className="flex-1 min-h-0 flex flex-col" data-testid="inline-discussion-thread">
        {disc && (
          <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2.5" data-testid="inline-discussion-header">
            <MessageSquare className="w-4 h-4 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-heading font-semibold text-foreground truncate">{disc.title || disc.question || 'Discussion'}</p>
              <p className="text-[10px] text-muted-foreground">{members.length} member{members.length === 1 ? '' : 's'}</p>
            </div>
            {members.length > 0 && (
              <div className="flex -space-x-2" data-testid="inline-discussion-avatars">
                {members.slice(0, 4).map(uid => (
                  <div key={uid} className="ring-2 ring-background rounded-full" title={nameOf(uid)}>
                    <UserAvatar name={nameOf(uid)} src={avatarOf(uid)} size={24} />
                  </div>
                ))}
                {members.length > 4 && (
                  <span className="w-6 h-6 rounded-full ring-2 ring-background bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground">+{members.length - 4}</span>
                )}
              </div>
            )}
            {disc.status === 'resolved' && <Badge className="text-[8px] bg-emerald-600 text-white border-emerald-700 shrink-0">Resolved</Badge>}
          </div>
        )}
        <div className="flex-1 min-h-0 flex flex-col p-4">
          {inner}
        </div>
      </div>
    );
  }

  if (floating) {
    if (!open) return null;
    return (
      <FloatingDiscussionShell status={disc?.status} onClose={() => onOpenChange(false)}>
        {inner}
      </FloatingDiscussionShell>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" /> Discussion
            {disc?.status === 'resolved' && <Badge className="text-[8px] bg-emerald-600 text-white border-emerald-700">Resolved</Badge>}
          </DialogTitle>
        </DialogHeader>
        {inner}
      </DialogContent>
    </Dialog>
  );
}

// A draggable, resizable floating window that renders the same thread content
// so users can view a resource and keep replying to its discussion.
function FloatingDiscussionShell({ children, onClose, status }) {
  const [pos, setPos] = useState({ x: Math.max(20, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 470), y: 88 });
  const drag = useRef(null);
  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    const move = (ev) => {
      if (!drag.current) return;
      setPos({ x: Math.max(0, drag.current.ox + ev.clientX - drag.current.sx), y: Math.max(0, drag.current.oy + ev.clientY - drag.current.sy) });
    };
    const up = () => { drag.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  return createPortal(
    <div
      className="fixed z-[100] flex flex-col rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden"
      style={{ left: pos.x, top: pos.y, width: 450, height: 620, maxHeight: '85vh', resize: 'both', pointerEvents: 'auto' }}
      data-testid="floating-discussion"
    >
      <div onMouseDown={onDown} className="flex items-center gap-2 px-3 py-2 border-b border-border/50 cursor-move bg-muted/20 select-none shrink-0">
        <GripVertical className="w-4 h-4 text-muted-foreground/70" />
        <MessageSquare className="w-4 h-4 text-primary" />
        <span className="text-sm font-heading font-semibold text-foreground">Discussion</span>
        {status === 'resolved' && <Badge className="text-[8px] bg-emerald-600 text-white border-emerald-700">Resolved</Badge>}
        <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid="floating-discussion-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-hidden flex flex-col p-3">{children}</div>
    </div>,
    document.body
  );
}

function OriginalQuestion({ disc, onOpenSource }) {
  const visibleEntries = Object.entries(disc.data_context || {}).filter(([k]) => !k.startsWith('_'));
  const src = disc.report_state?.source;
  const sourceLabel = src === 'explore'
    ? 'Open in AI Explore'
    : src === 'insight'
      ? 'Open Insight'
      : src === 'investigation'
        ? 'Reopen Investigation'
        : disc.report_id
          ? 'Open Report'
          : null;
  return (
    <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 mb-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary">{disc.created_by_name?.[0] || 'U'}</div>
        <span className="text-xs font-semibold text-foreground">{disc.created_by_name}</span>
        <span className="text-[9px] text-muted-foreground ml-auto">{fmtDate(disc.created_at)}</span>
      </div>
      {disc.title && <p className="text-sm font-heading font-semibold text-foreground mb-1" data-testid="discussion-title">{disc.title}</p>}
      <p className="text-sm text-foreground">{disc.question}</p>
      {disc.report_state?.prompt && src === 'explore' && (
        <div className="mt-2 rounded-lg bg-indigo-500/5 border border-indigo-500/15 px-2 py-1.5" data-testid="discussion-original-prompt">
          <p className="text-[9px] text-indigo-400/70 uppercase tracking-wider font-semibold">Original prompt</p>
          <p className="text-[11px] text-foreground/90 mt-0.5 italic">&ldquo;{disc.report_state.prompt}&rdquo;</p>
        </div>
      )}
      {disc.data_context && Object.keys(disc.data_context).length > 0 && (
        <div className="mt-2 space-y-1.5">
          {visibleEntries.length > 0 && (
            <div className="space-y-0.5">
              {visibleEntries.map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-[10px]">
                  <span className="text-muted-foreground">{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                  <span className="text-foreground font-semibold">{typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v)}</span>
                </div>
              ))}
            </div>
          )}
          {disc.data_context._filters && <div className="flex flex-wrap gap-1">{Object.entries(disc.data_context._filters).map(([k, v]) => <Badge key={k} className="text-[7px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">Filter: {k} = {String(v)}</Badge>)}</div>}
          {disc.data_context._cross_filter && <Badge className="text-[7px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">{disc.data_context._cross_filter}</Badge>}
        </div>
      )}
      {disc.screenshot && <img src={disc.screenshot} alt="Snapshot" className="mt-2 rounded-lg border border-border/30 max-h-28 w-full object-cover" />}
      <AttachmentDisplay attachments={disc.attachments} />
      {sourceLabel && onOpenSource && (
        <Button
          size="sm"
          variant="outline"
          onClick={onOpenSource}
          className="mt-2.5 h-7 rounded-lg text-[10px] gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
          data-testid="discussion-open-source-btn"
        >
          <ExternalLink className="w-3 h-3" />
          {sourceLabel}
        </Button>
      )}
    </div>
  );
}
